import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect } from "react";
import { Grid } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import { useRestrictedOptions } from "../BaseFilterComponents/useRestrictedOptions";

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;

/**
 * Themes resolved through the Primary Driver, persisted under the "themes" filter key and
 * governed by Drivers/View. Mirrors {@link TopicsFilter}, which is the legacy Fast Reporting
 * Theme filter under the "topics" key; the two never merge or fall back to one another.
 */
export const ThemesFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const canViewDrivers = authStore.canUserView("Drivers");
    const isThemesLoading = valuesStore.getTaskLoading(
        ApplicationFilterValuesStore.Tasks.LOAD_DRIVER_THEMES,
    );
    const { availableOptions: availableThemes } = useRestrictedOptions({
        options: valuesStore.driverThemes,
        getOptionValue: (theme) => theme.id,
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const allowedModes =
        props.modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is one of",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not one of",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];

    useEffect(() => {
        if (!props.modeRestriction) return;
        const currentMode = props.store.containsThemes ? "include" : "exclude";
        if (allowedModes.includes(currentMode)) return;
        props.store.setContainsThemes(allowedModes[0] === "include");
    }, [allowedModes, props.modeRestriction, props.store]);

    useEffect(() => {
        if (!canViewDrivers) return;

        void valuesStore.loadDriverThemes();
    }, [canViewDrivers, valuesStore]);

    if (!canViewDrivers) return null;

    return (
        <FilterSectionContainer>
            <Grid container item direction="column">
                <Grid item>
                    <AcxSelectSingle
                        id={"application-filters-driver-themes-contains"}
                        options={modeOptions}
                        labelField="label"
                        valueField="value"
                        defaultValue={{
                            label: props.store.containsThemesString,
                            value: props.store.containsThemes,
                        }}
                        customStyle={customFilterSelectStyles}
                        onChange={(item) => {
                            if (item.isDisabled) return;
                            props.store.setContainsThemes(item.value);
                        }}
                        isDisabled={
                            modeOptions.filter((item) => !item.isDisabled)
                                .length <= 1
                        }
                    />
                </Grid>
                <Grid item>
                    <AcxSelectMulti
                        id="application-filters-driver-themes"
                        fullWidth
                        isLoading={isThemesLoading}
                        isDisabled={isThemesLoading}
                        options={availableThemes}
                        defaultValue={props.store.themes}
                        onChange={(options) => {
                            props.store.setThemes(!!options ? options : []);
                        }}
                        valueField="id"
                        labelField="themeLabel"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                </Grid>
            </Grid>
        </FilterSectionContainer>
    );
});
